import React from "react";
import { X, CornerUpLeft } from "lucide-react";
import { FeedbackSender, MessageReplyTo } from "../../types/feedback";
import { TicketAttachment } from "../../types/ticket";
import { isVideoAttachment } from "./attachment-gallery";

// previewOf builds the short snapshot shown in a quote, falling back to a media
// label for attachment-only messages.
export const previewOf = (m: {
  body?: string;
  attachments?: TicketAttachment[];
}): string => {
  if (m.body && m.body.trim()) return m.body.trim();
  const a = m.attachments?.[0];
  if (a) return isVideoAttachment(a) ? "🎥 Video" : "📷 Photo";
  return "Message";
};

// labelFor names the quoted author from the viewer's perspective.
export const labelFor = (sender: FeedbackSender, viewerSide: FeedbackSender) => {
  if (sender === viewerSide) return "You";
  return sender === "admin" ? "Weddn" : "Customer";
};

// QuotedBlock renders the quoted message inside a reply bubble.
export const QuotedBlock: React.FC<{
  replyTo: MessageReplyTo;
  viewerSide: FeedbackSender;
}> = ({ replyTo, viewerSide }) => (
  <div className="mb-1 border-l-2 border-[#1E3A8A] bg-black/5 rounded px-2 py-1">
    <p className="text-[11px] font-semibold text-[#1E3A8A]">
      {labelFor(replyTo.sender, viewerSide)}
    </p>
    <p className="text-[11px] text-gray-600 line-clamp-2 break-words">
      {replyTo.preview}
    </p>
  </div>
);

// ReplyingToBar sits above the composer while drafting a reply.
export const ReplyingToBar: React.FC<{
  target: MessageReplyTo;
  viewerSide: FeedbackSender;
  onCancel: () => void;
}> = ({ target, viewerSide, onCancel }) => (
  <div className="flex items-start gap-2 mb-2 border-l-2 border-[#1E3A8A] bg-gray-100 rounded px-3 py-2">
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold text-[#1E3A8A]">
        Replying to {labelFor(target.sender, viewerSide)}
      </p>
      <p className="text-[11px] text-gray-600 line-clamp-1 break-words">
        {target.preview}
      </p>
    </div>
    <button onClick={onCancel} aria-label="Cancel reply" className="mt-0.5">
      <X size={14} className="text-gray-500" />
    </button>
  </div>
);

// ReplyButton is the small affordance shown next to a message to quote-reply it.
export const ReplyButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    aria-label="Reply"
    className="self-center p-1 text-gray-400 hover:text-gray-600 opacity-60 hover:opacity-100 transition-opacity"
  >
    <CornerUpLeft size={15} />
  </button>
);
